import {
  VideoQuality,
  VideoSurface,
  type Participant,
  type ZoomMediaStream,
} from '@fireflyhealth/zoom-web'
import React from 'react'
import { logger } from '~/shared/lib/logger'
import { SessionVideoTile } from './SessionVideoTile'

/**
 * Props interface for the SessionVideoPlayer component
 * Defines the required properties for rendering an individual participant's video stream
 */
interface SessionVideoPlayerProps {
  /** Participant object containing user information and video/audio state */
  user: Participant
  /** Zoom Stream API instance for managing video stream attachment/detachment */
  stream: ZoomMediaStream
  /** Network quality score for this user (0-3, where 3 is best quality) */
  networkQuality: number | undefined
  /** Flag indicating if this is the spotlight video (affects sizing and display behavior) */
  isSpotlightVideo: boolean
}

/**
 * SessionVideoPlayer Component
 *
 * Individual video player for a single participant. The Zoom `<video-player>` tree, the attach
 * lifecycle and the "never detach" rule live in the shared `VideoSurface` (`@fireflyhealth/zoom-web`);
 * this component supplies CRM's sizing and stacks the `SessionVideoTile` status overlay on top.
 *
 * The spotlight fills its container; every other tile is a fixed-height, auto-width
 * picture-in-picture. Both request 1080p regardless of rendered size.
 */
export const SessionVideoPlayer: React.FC<SessionVideoPlayerProps> = ({
  user,
  stream,
  networkQuality,
  isSpotlightVideo,
}) => {
  const playerStyle = isSpotlightVideo
    ? { height: '100%', width: '100%', zIndex: 100 }
    : { height: '125px', width: 'auto', zIndex: 100 }

  return (
    <>
      <VideoSurface
        stream={stream}
        userId={user.userId}
        videoOn={!!user.bVideoOn}
        quality={VideoQuality.Video_1080P}
        style={playerStyle}
        onError={error => logger.warn('Zoom: attach video failed', error)}
      />
      {/*
        Overlay tile showing participant status information
        Displays mute status and network quality indicators over the video
      */}
      <SessionVideoTile muted={!!user.muted} quality={networkQuality} />
    </>
  )
}
